import { useState, useRef } from 'react'
import Logo from '../components/Logo'

function go(path) {
  window.location.href = path
}

export default function Landing() {
  return (
    <div style={{ background: 'white' }}>
      <Hero />
      <MatchTrackerSection />
      <ScreenshotSection />
      <FeaturesSection />
      <FinalCta />
      <Footer />
    </div>
  )
}

function Hero() {
  return (
    <div style={{ background: '#185FA5', padding: '48px 20px 56px' }}>
      <div style={{ maxWidth: '1080px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '40px', flexWrap: 'wrap' }}>
        <div style={{ flex: '0 1 360px', minWidth: '240px', display: 'flex', justifyContent: 'center', order: 1 }}>
          <Logo variant="light" width="100%" maxWidth="360px" />
        </div>

        <div style={{ flex: '1 1 420px', minWidth: '280px', order: 2 }}>
          <h1 style={{ color: 'white', fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: '600', lineHeight: '1.2', margin: '0 0 14px' }}>
            Every score, the moment it happens.
          </h1>
          <p style={{ color: '#B5D4F4', fontSize: '16px', lineHeight: '1.6', margin: '0 0 24px', maxWidth: '460px' }}>
            Coaches track the match pitch-side. Parents, supporters, and coaches who can't make it follow along live — no app to download, no login needed.
          </p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => go('/login?mode=signup')} style={heroBtnPrimary}>Register your club</button>
            <button type="button" onClick={() => go('/login')} style={heroBtnGhost}>Sign in</button>
            <button type="button" onClick={() => go('/help')} style={heroBtnGhost}>Help</button>
          </div>
        </div>
      </div>
    </div>
  )
}

function MatchTrackerSection() {
  return (
    <div style={{ background: '#f8f8f8', padding: '56px 20px' }}>
      <div style={{ maxWidth: '1080px', margin: '0 auto' }}>
        <SectionEyebrow text="THE MATCH TRACKER" />
        <h2 style={sectionHeading}>Every detail of your gaelic games, tracked live.</h2>
        <p style={sectionSubhead}>Goals and points for both sides, kick outs, frees, cards and subs, plus a pitch view that always shows who's on — organised into four simple tabs.</p>

        {/* Desktop: all four tabs side by side */}
        <div className="mt-desktop-row" style={{ display: 'none', gap: '20px', marginTop: '32px' }}>
          <PhoneFrame label="Score"><ScoreTabMock /></PhoneFrame>
          <PhoneFrame label="Pitch"><PitchTabMock /></PhoneFrame>
          <PhoneFrame label="Squad"><SquadTabMock /></PhoneFrame>
          <PhoneFrame label="Events"><EventsTabMock /></PhoneFrame>
        </div>

        {/* Mobile: tab switcher */}
        <div className="mt-mobile-tabs" style={{ display: 'flex', justifyContent: 'center', marginTop: '32px' }}>
          <MobileTabSwitcher />
        </div>

        <style>{`
          @media (min-width: 768px) {
            .mt-desktop-row { display: flex !important; }
            .mt-mobile-tabs { display: none !important; }
          }
        `}</style>
      </div>
    </div>
  )
}

function PhoneFrame({ label, children }) {
  return (
    <div style={{ flex: '1 1 0', minWidth: 0 }}>
      <div style={{ background: 'white', borderRadius: '16px', border: '0.5px solid #e0e0e0', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
        {children}
      </div>
      <p style={{ textAlign: 'center', fontSize: '12px', color: '#888', marginTop: '12px' }}>{label}</p>
    </div>
  )
}

function MobileTabSwitcher() {
  const [tab, setTab] = useState('score')
  const tabs = [
    { id: 'score', label: 'Score', icon: 'ti-ball-football' },
    { id: 'pitch', label: 'Pitch', icon: 'ti-stadium' },
    { id: 'squad', label: 'Squad', icon: 'ti-users' },
    { id: 'events', label: 'Events', icon: 'ti-clipboard-list' },
  ]
  const touchStartX = useRef(null)

  function handleTouchStart(e) { touchStartX.current = e.touches[0].clientX }
  function handleTouchEnd(e) {
    if (touchStartX.current === null) return
    const diff = touchStartX.current - e.changedTouches[0].clientX
    if (Math.abs(diff) > 50) {
      const idx = tabs.findIndex(t => t.id === tab)
      if (diff > 0 && idx < tabs.length - 1) setTab(tabs[idx + 1].id)
      else if (diff < 0 && idx > 0) setTab(tabs[idx - 1].id)
    }
    touchStartX.current = null
  }

  return (
    <div style={{ background: 'white', borderRadius: '18px', border: '0.5px solid #e0e0e0', overflow: 'hidden', width: '100%', maxWidth: '300px', boxShadow: '0 8px 30px rgba(0,0,0,0.08)' }}>
      <div style={{ display: 'flex', borderBottom: '0.5px solid #e0e0e0' }}>
        {tabs.map(t => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
            style={{ flex: 1, padding: '9px 0', background: 'none', border: 'none', cursor: 'pointer', borderBottom: tab === t.id ? '2px solid #185FA5' : '2px solid transparent', pointerEvents: 'auto' }}>
            <span style={{ pointerEvents: 'none' }}>
              <TabIcon name={t.icon} active={tab === t.id} />
              <div style={{ fontSize: '9px', color: tab === t.id ? '#185FA5' : '#999', marginTop: '3px' }}>{t.label}</div>
            </span>
          </button>
        ))}
      </div>
      <div onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
        {tab === 'score' && <ScoreTabMock />}
        {tab === 'pitch' && <PitchTabMock />}
        {tab === 'squad' && <SquadTabMock />}
        {tab === 'events' && <EventsTabMock />}
      </div>
    </div>
  )
}

function TabIcon({ name, active }) {
  const color = active ? '#185FA5' : '#999'
  const icons = {
    'ti-ball-football': (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" style={{ margin: '0 auto', display: 'block' }}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7l3.5 2.5-1.3 4H9.8l-1.3-4z" />
      </svg>
    ),
    'ti-stadium': (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" style={{ margin: '0 auto', display: 'block' }}>
        <rect x="4" y="5" width="16" height="14" rx="1" />
        <line x1="12" y1="5" x2="12" y2="19" />
        <circle cx="12" cy="12" r="2.5" />
      </svg>
    ),
    'ti-users': (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" style={{ margin: '0 auto', display: 'block' }}>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 19c0-3 2.7-5 6-5s6 2 6 5" />
        <circle cx="17" cy="9" r="2.5" />
        <path d="M15.5 14.2c2.3.4 4 2 4 4.3" />
      </svg>
    ),
    'ti-clipboard-list': (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" style={{ margin: '0 auto', display: 'block' }}>
        <rect x="6" y="4" width="12" height="17" rx="1.5" />
        <rect x="9" y="2.5" width="6" height="3" rx="1" />
        <line x1="9" y1="11" x2="15" y2="11" />
        <line x1="9" y1="15" x2="15" y2="15" />
      </svg>
    ),
  }
  return icons[name] || null
}

function ScoreTabMock() {
  return (
    <div>
      <div style={{ background: '#185FA5', padding: '10px 12px' }}>
        <p style={{ textAlign: 'center', fontSize: '9px', color: '#B5D4F4', margin: '0 0 4px' }}>Football · Team 1</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{ fontSize: '9px', color: '#B5D4F4', margin: 0 }}>Us</p>
            <p style={{ fontSize: '18px', fontWeight: '600', color: 'white', margin: 0 }}>2-14</p>
          </div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{ fontSize: '9px', color: '#B5D4F4', margin: '0 0 1px' }}>2nd Half</p>
            <p style={{ fontSize: '14px', fontWeight: '600', color: 'white', margin: '0 0 3px' }}>28:41</p>
            <div style={{ display: 'flex', gap: '3px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '7px', padding: '1px 5px', borderRadius: '8px', background: 'rgba(255,255,255,0.2)', color: 'white' }}>Pause</span>
              <span style={{ fontSize: '7px', padding: '1px 5px', borderRadius: '8px', background: 'rgba(255,255,255,0.2)', color: 'white' }}>Reset</span>
              <span style={{ fontSize: '7px', padding: '1px 5px', borderRadius: '8px', background: 'rgba(255,80,80,0.35)', color: 'white' }}>End</span>
            </div>
          </div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{ fontSize: '9px', color: '#B5D4F4', margin: 0 }}>Cuala</p>
            <p style={{ fontSize: '18px', fontWeight: '600', color: 'white', margin: 0 }}>1-11</p>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: '6px', fontSize: '8px', background: 'rgba(255,255,255,0.15)', borderRadius: '10px', padding: '2px 8px', color: 'white' }}>Lead by 6 pts</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr', padding: '9px 8px 4px' }}>
        <ScoreColumn label="Us" goalColor="#185FA5" goalBg="#E6F1FB" wideColor="#854F0B" wideBg="#FAEEDA" wideCount={3} />
        <div style={{ background: '#e8e8e8', margin: '16px 0 0' }} />
        <ScoreColumn label="Cuala" goalColor="#A32D2D" goalBg="#FCEBEB" wideColor="#888" wideBg="#f5f5f5" wideCount={2} />
      </div>

      <div style={{ padding: '6px 8px 0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
        <StatTile bg="#EAF3DE" color="#3B6D11" label="Our Kick Outs" value="4W 1L" />
        <StatTile bg="#E1F5EE" color="#0F6E56" label="Free Won" value="3 won · 2 con" />
        <StatTile bg="#f5f5f5" color="#888" label="Opp Kick Outs" value="3W 2L" />
        <StatTile bg="#f5f5f5" color="#888" label="Free Conceded" value="2" />
      </div>

      <div style={{ padding: '6px 8px 0', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '4px' }}>
        <span style={{ background: '#FAEEDA', color: '#854F0B', borderRadius: '6px', padding: '5px 0', textAlign: 'center', fontSize: '8px' }}>Card</span>
        <span style={{ background: '#EEEDFE', color: '#533AB7', borderRadius: '6px', padding: '5px 0', textAlign: 'center', fontSize: '8px' }}>Sub</span>
        <span style={{ background: '#FCEBEB', color: '#A32D2D', borderRadius: '6px', padding: '5px 0', textAlign: 'center', fontSize: '8px' }}>Undo</span>
      </div>

      <div style={{ padding: '8px 8px 12px', borderTop: '0.5px solid #f0f0f0', marginTop: '8px' }}>
        <p style={{ fontSize: '8px', color: '#888', margin: '0 0 4px' }}>SCORERS</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', padding: '1px 0' }}>
          <span style={{ color: '#333' }}>J. Byrne</span><span style={{ fontWeight: '500', color: '#185FA5' }}>1-04</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '9px', padding: '1px 0' }}>
          <span style={{ color: '#333' }}>C. Walsh</span><span style={{ fontWeight: '500', color: '#185FA5' }}>1-02</span>
        </div>
      </div>
    </div>
  )
}

function ScoreColumn({ label, goalColor, goalBg, wideColor, wideBg, wideCount }) {
  return (
    <div>
      <p style={{ fontSize: '8px', color: '#888', fontWeight: '500', textAlign: 'center', margin: '0 0 6px' }}>{label}</p>
      {['Goal', 'Point'].map(t => (
        <div key={t} style={{ display: 'flex', alignItems: 'center', gap: '3px', marginBottom: '4px' }}>
          <span style={{ width: '15px', height: '15px', borderRadius: '4px', background: goalBg, color: goalColor, fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>+</span>
          <span style={{ flex: 1, fontSize: '8px', color: '#333', textAlign: 'center' }}>{t}</span>
          <span style={{ width: '15px', height: '15px', borderRadius: '4px', background: '#f5f5f5', color: '#888', fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>−</span>
        </div>
      ))}
      <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
        <span style={{ width: '15px', height: '15px', borderRadius: '4px', background: wideBg, color: wideColor, fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>+</span>
        <span style={{ flex: 1, fontSize: '8px', color: '#333', textAlign: 'center' }}>Wide ({wideCount})</span>
        <span style={{ width: '15px', height: '15px', borderRadius: '4px', background: '#f5f5f5', color: '#888', fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>−</span>
      </div>
    </div>
  )
}

function StatTile({ bg, color, label, value }) {
  return (
    <div style={{ background: bg, color, borderRadius: '6px', padding: '5px 3px', textAlign: 'center', fontSize: '7px', lineHeight: '1.4' }}>
      {label}<br /><span style={{ fontWeight: '500' }}>{value}</span>
    </div>
  )
}

const PITCH_POSITIONS = [
  { n: 1, x: 50, y: 90, name: 'Murphy' }, { n: 2, x: 80, y: 76, name: 'Smith' }, { n: 3, x: 50, y: 76, name: 'Doyle' },
  { n: 4, x: 20, y: 76, name: 'Kavanagh' }, { n: 5, x: 80, y: 60, name: 'Ryan' }, { n: 6, x: 50, y: 60, name: 'Healy' },
  { n: 7, x: 20, y: 60, name: 'Walsh' }, { n: 8, x: 33, y: 45, name: 'Nolan' }, { n: 9, x: 67, y: 45, name: 'Byrne' },
  { n: 10, x: 80, y: 30, name: 'Brennan' }, { n: 11, x: 50, y: 28, name: 'Kelly' }, { n: 12, x: 20, y: 30, name: 'Connolly' },
  { n: 13, x: 80, y: 12, name: 'Quinn' }, { n: 14, x: 50, y: 8, name: 'Lynch' }, { n: 15, x: 20, y: 12, name: 'Dunne' },
]

function PitchTabMock() {
  return (
    <div>
      <div style={{ background: '#185FA5', padding: '6px 0', textAlign: 'center', fontSize: '9px', color: '#B5D4F4' }}>2-14 · 28:41 · 1-11</div>
      <div style={{ padding: '8px' }}>
        <div style={{ position: 'relative', height: '270px', background: '#2d7a3a', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: '4px', border: '0.5px solid rgba(255,255,255,0.5)' }} />
          <div style={{ position: 'absolute', left: '3%', right: '3%', top: '50%', height: '0.5px', background: 'rgba(255,255,255,0.5)' }} />
          {PITCH_POSITIONS.map(p => (
            <div key={p.n} style={{ position: 'absolute', left: p.x + '%', top: p.y + '%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#185FA5', border: '1.5px solid white', color: 'white', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '500', margin: '0 auto 1px' }}>{p.n}</div>
              <div style={{ fontSize: '6.5px', color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>{p.name}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const STARTERS = [
  { num: '1', name: 'J. Murphy', pos: 'Goalkeeper', score: null },
  { num: '7', name: 'C. Walsh', pos: 'Left Half Back', score: '1-02' },
  { num: '9', name: 'J. Byrne', pos: 'Midfield', score: '1-04' },
  { num: '11', name: 'D. Kelly', pos: 'Centre Forward', score: '0-05' },
]

function SquadTabMock() {
  return (
    <div>
      <div style={{ background: '#185FA5', padding: '6px 0', textAlign: 'center', fontSize: '9px', color: '#B5D4F4' }}>2-14 · 28:41 · 1-11</div>
      <p style={{ fontSize: '8px', color: '#888', padding: '8px 12px 3px', margin: 0 }}>STARTERS</p>
      {STARTERS.map(p => (
        <div key={p.num} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderBottom: '0.5px solid #f5f5f5' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#E6F1FB', color: '#185FA5', fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '500', flexShrink: 0 }}>{p.num}</div>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: '10px', margin: 0, color: '#333' }}>{p.name}</p>
            <p style={{ fontSize: '8px', margin: 0, color: '#999' }}>{p.pos}</p>
          </div>
          {p.score && <span style={{ fontSize: '9px', fontWeight: '500', color: '#185FA5' }}>{p.score}</span>}
        </div>
      ))}
      <p style={{ fontSize: '8px', color: '#888', padding: '8px 12px 3px', margin: 0 }}>SUBSTITUTES</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px 10px' }}>
        <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#EEEDFE', color: '#533AB7', fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '500', flexShrink: 0 }}>17</div>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '10px', margin: 0, color: '#333' }}>L. Doyle</p>
          <p style={{ fontSize: '8px', margin: 0, color: '#999' }}>Sub</p>
        </div>
      </div>
    </div>
  )
}

function EventsTabMock() {
  const rows = [
    { section: 'GOALS', color: '#185FA5', items: [["14'", 'Our goal · J. Byrne'], ["31'", 'Opp goal']] },
    { section: 'POINTS', color: '#3B6D11', items: [["9'", 'Point · D. Kelly'], ["22'", 'Point · C. Walsh']] },
    { section: 'KICK OUTS', color: '#854F0B', items: [["26'", 'Kick out won · J. Murphy']] },
    { section: 'SUBSTITUTIONS', color: '#533AB7', items: [["35'", 'Sub on · L. Doyle']] },
  ]
  return (
    <div>
      <div style={{ background: '#185FA5', padding: '6px 0', textAlign: 'center', fontSize: '9px', color: '#B5D4F4' }}>2-14 · 28:41 · 1-11</div>
      <div style={{ padding: '10px 12px' }}>
        {rows.map(r => (
          <div key={r.section} style={{ marginBottom: '10px' }}>
            <p style={{ fontSize: '8px', color: r.color, fontWeight: '500', margin: '0 0 4px' }}>{r.section}</p>
            {r.items.map(([min, text]) => (
              <div key={min + text} style={{ display: 'flex', gap: '6px', fontSize: '9px', padding: '2px 0', color: '#333' }}>
                <span style={{ color: '#aaa', minWidth: '20px' }}>{min}</span><span>{text}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function ScreenshotSection() {
  return (
    <div style={{ maxWidth: '1080px', margin: '0 auto', padding: '56px 20px' }}>
      <SectionEyebrow text="FOR COACHES & SUPPORTERS" />
      <h2 style={sectionHeading}>Fixtures, results and live scores.</h2>
      <p style={sectionSubhead}>Coaches log the action as it happens. Supporters follow along online.</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px', marginTop: '32px' }}>
        <FixturesShot />
        <PublicPageShot />
        <LiveViewShot />
      </div>
    </div>
  )
}

function FixturesShot() {
  return (
    <ShotFrame caption="Coach view — fixtures">
      <div style={{ padding: '12px 12px 8px', borderBottom: '0.5px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: '13px', fontWeight: '500', color: '#1a1a1a' }}>Fixtures</span>
        <span style={{ fontSize: '10px', padding: '3px 9px', borderRadius: '14px', background: '#185FA5', color: 'white' }}>+ Add</span>
      </div>
      <div style={{ display: 'flex', borderBottom: '0.5px solid #f0f0f0' }}>
        <div style={{ flex: 1, textAlign: 'center', padding: '6px 0', fontSize: '10px', color: '#185FA5', fontWeight: '500', borderBottom: '2px solid #185FA5' }}>Upcoming</div>
        <div style={{ flex: 1, textAlign: 'center', padding: '6px 0', fontSize: '10px', color: '#999' }}>Results</div>
      </div>
      <div style={{ padding: '10px 12px', borderBottom: '0.5px solid #f5f5f5' }}>
        <p style={{ fontSize: '11px', fontWeight: '500', margin: '0 0 2px', color: '#1a1a1a' }}>vs Cuala</p>
        <p style={{ fontSize: '9px', color: '#999', margin: '0 0 3px' }}>U13 Boys · Football</p>
        <p style={{ fontSize: '8px', color: '#aaa', margin: '0 0 6px' }}>Sat 20 Jun · 11:00 · St Mary's Park</p>
        <span style={{ fontSize: '8px', padding: '2px 7px', borderRadius: '10px', background: '#FF3B30', color: 'white', fontWeight: '500' }}>LIVE · HT</span>
      </div>
      <div style={{ padding: '10px 12px', borderBottom: '0.5px solid #f5f5f5' }}>
        <p style={{ fontSize: '11px', fontWeight: '500', margin: '0 0 2px', color: '#1a1a1a' }}>vs St Judes</p>
        <p style={{ fontSize: '9px', color: '#999', margin: '0 0 3px' }}>U13 Boys · Football</p>
        <p style={{ fontSize: '8px', color: '#aaa', margin: '0 0 6px' }}>Sat 27 Jun · 14:00 · Tymon Park</p>
        <span style={{ fontSize: '8px', padding: '2px 7px', borderRadius: '10px', background: '#FAEEDA', color: '#854F0B', fontWeight: '500' }}>Upcoming</span>
      </div>
      <div style={{ padding: '10px 12px' }}>
        <p style={{ fontSize: '11px', fontWeight: '500', margin: '0 0 2px', color: '#1a1a1a' }}>vs Na Fianna</p>
        <p style={{ fontSize: '9px', color: '#999', margin: '0 0 3px' }}>U13 Boys · Hurling</p>
        <p style={{ fontSize: '8px', color: '#aaa', margin: '0 0 6px' }}>Sat 4 Jul · 10:30 · Mobhi Road</p>
        <span style={{ fontSize: '8px', padding: '2px 7px', borderRadius: '10px', background: '#EAF3DE', color: '#3B6D11', fontWeight: '500' }}>✓ Lineup Confirmed</span>
      </div>
    </ShotFrame>
  )
}

function PublicPageShot() {
  return (
    <ShotFrame caption="Public page — no login needed">
      <div style={{ background: '#185FA5', padding: '14px', textAlign: 'center' }}>
        <p style={{ fontSize: '13px', fontWeight: '500', color: 'white', margin: 0 }}>U13 Boys</p>
        <p style={{ fontSize: '9px', color: '#B5D4F4', margin: '2px 0 0' }}>Naomh Mearnog</p>
      </div>
      <div style={{ background: '#A32D2D', padding: '9px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ff6b6b', display: 'inline-block' }} />
          <span style={{ fontSize: '10px', color: 'white', fontWeight: '500' }}>HT vs Cuala</span>
        </div>
        <span style={{ fontSize: '9px', color: 'white' }}>2-14 · 1-11</span>
      </div>
      <div style={{ background: '#E6F1FB', padding: '8px 12px' }}>
        <p style={{ fontSize: '8px', color: '#185FA5', fontWeight: '500', margin: '0 0 2px' }}>NEXT FIXTURE</p>
        <p style={{ fontSize: '11px', color: '#185FA5', margin: 0 }}>vs St Judes · Sat 14:00</p>
      </div>
      <div style={{ display: 'flex', borderBottom: '0.5px solid #e0e0e0', background: 'white' }}>
        <div style={{ flex: 1, textAlign: 'center', padding: '7px 0', fontSize: '9px', color: '#185FA5', borderBottom: '2px solid #185FA5' }}>Fixtures</div>
        <div style={{ flex: 1, textAlign: 'center', padding: '7px 0', fontSize: '9px', color: '#999' }}>Results</div>
        <div style={{ flex: 1, textAlign: 'center', padding: '7px 0', fontSize: '9px', color: '#999' }}>Scorers</div>
      </div>
      <div style={{ background: 'white', padding: '18px 12px', textAlign: 'center' }}>
        <p style={{ fontSize: '9px', color: '#888', margin: 0 }}>Tap the red banner to follow live →</p>
      </div>
    </ShotFrame>
  )
}

function LiveViewShot() {
  return (
    <ShotFrame caption="Live view at half time">
      <div style={{ background: '#f0f0f0', padding: '8px 12px', fontSize: '10px', color: '#666' }}>← Back to fixtures</div>
      <div style={{ background: '#185FA5', padding: '14px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{ fontSize: '9px', color: '#B5D4F4', margin: '0 0 3px' }}>U13 Boys</p>
            <p style={{ fontSize: '24px', fontWeight: '600', color: 'white', margin: 0 }}>2-14</p>
          </div>
          <div style={{ textAlign: 'center', padding: '0 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center', marginBottom: '3px' }}>
              <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#ff6b6b', display: 'inline-block' }} />
              <span style={{ fontSize: '9px', color: '#B5D4F4' }}>Half Time</span>
            </div>
            <p style={{ fontSize: '14px', fontWeight: '600', color: 'white', margin: 0 }}>00:00</p>
          </div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{ fontSize: '9px', color: '#B5D4F4', margin: '0 0 3px' }}>Cuala</p>
            <p style={{ fontSize: '24px', fontWeight: '600', color: 'white', margin: 0 }}>1-11</p>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '9px', background: 'rgba(255,255,255,0.15)', borderRadius: '12px', padding: '3px 10px', color: 'white' }}>Leading by 6 pts</div>
      </div>
      <div style={{ padding: '10px 12px' }}>
        <p style={{ fontSize: '8px', color: '#888', fontWeight: '500', margin: '0 0 6px' }}>SCORERS</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', padding: '2px 0' }}>
          <span style={{ color: '#333' }}>J. Byrne</span><span style={{ fontWeight: '500', color: '#185FA5' }}>1-04</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', padding: '2px 0' }}>
          <span style={{ color: '#333' }}>C. Walsh</span><span style={{ fontWeight: '500', color: '#185FA5' }}>1-02</span>
        </div>
      </div>
      <p style={{ textAlign: 'center', fontSize: '8px', color: '#aaa', padding: '6px 0 10px' }}>Live · updates every 10s</p>
    </ShotFrame>
  )
}

function ShotFrame({ caption, children }) {
  return (
    <div>
      <div style={{ background: 'white', borderRadius: '16px', border: '0.5px solid #e0e0e0', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
        {children}
      </div>
      <p style={{ textAlign: 'center', fontSize: '12px', color: '#888', marginTop: '12px' }}>{caption}</p>
    </div>
  )
}

function FeaturesSection() {
  const features = [
    { title: 'Live match clock', text: 'Start, pause, half time — synced across every screen watching.' },
    { title: 'One tracker at a time', text: "The match locks to whoever's tracking it, so two phones can't clash mid-game." },
    { title: 'One club, every team', text: 'Add mentors for each age group — club admins see across all of them.' },
    { title: 'Follow from anywhere', text: 'Stuck at work or minding another game? Coaches and supporters can follow live from anywhere.' },
    { title: 'Every county, every code', text: 'Football, hurling and camogie — built for clubs and every age group nationwide.' },
    { title: 'Full match detail', text: 'Kick outs, frees, wides, cards and subs — not just the final score.' },
  ]
  return (
    <div style={{ maxWidth: '1080px', margin: '0 auto', padding: '56px 20px' }}>
      <SectionEyebrow text="WHY CLUBS USE IT" />
      <h2 style={sectionHeading}>Built for how clubs actually run.</h2>
      <ul style={{ listStyle: 'none', margin: '28px 0 0', padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '18px 32px' }}>
        {features.map(f => (
          <li key={f.title} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#185FA5', marginTop: '8px', flexShrink: 0 }} />
            <div>
              <p style={{ fontSize: '15px', fontWeight: '500', margin: '0 0 3px' }}>{f.title}</p>
              <p style={{ fontSize: '13px', color: '#888', margin: 0, lineHeight: '1.55' }}>{f.text}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function FinalCta() {
  return (
    <div style={{ background: '#0d3d6b', padding: '56px 20px', textAlign: 'center' }}>
      <h2 style={{ color: 'white', fontSize: '26px', fontWeight: '600', margin: '0 0 12px' }}>Ready to track your next match?</h2>
      <p style={{ color: '#B5D4F4', fontSize: '15px', margin: '0 0 24px' }}>Free to set up. Your club, your squads, your scores.</p>
      <button type="button" onClick={() => go('/login?mode=signup')} style={{ ...heroBtnPrimary, padding: '13px 28px', fontSize: '15px' }}>Register your club</button>
    </div>
  )
}

function Footer() {
  return (
    <div style={{ padding: '24px 20px', textAlign: 'center', borderTop: '0.5px solid #e0e0e0' }}>
      <p style={{ fontSize: '12px', color: '#aaa', margin: '0 0 6px' }}>GAA Match Tracker</p>
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
        <button type="button" onClick={() => go('/contact')} style={footerLinkBtn}>Contact us</button>
        <button type="button" onClick={() => go('/privacy')} style={footerLinkBtn}>Privacy</button>
        <button type="button" onClick={() => go('/terms')} style={footerLinkBtn}>Terms</button>
      </div>
    </div>
  )
}

function SectionEyebrow({ text }) {
  return <p style={{ fontSize: '11px', fontWeight: '600', color: '#185FA5', letterSpacing: '0.6px', margin: '0 0 10px' }}>{text}</p>
}

const sectionHeading = { fontSize: 'clamp(22px, 3.5vw, 28px)', fontWeight: '600', margin: '0 0 10px', maxWidth: '560px' }
const sectionSubhead = { fontSize: '15px', color: '#888', margin: 0, lineHeight: '1.6', maxWidth: '520px' }

const heroBtnPrimary = { padding: '12px 20px', background: 'white', color: '#185FA5', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }
const heroBtnGhost = { padding: '12px 20px', background: 'transparent', color: 'white', border: '0.5px solid rgba(255,255,255,0.5)', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }
const footerLinkBtn = { background: 'none', border: 'none', color: '#185FA5', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }